import { useMemo, useRef, useState } from 'react';
import { Header } from '../components/Header';
import { ValuePanel } from '../components/ValuePanel';
import { Donut, type Slice } from '../components/Donut';
import { WeightBars } from '../components/WeightBars';
import { HoldingsList } from '../components/HoldingsList';
import { ThemeBars } from '../components/ThemeBars';
import { Alerts } from '../components/Alerts';
import { ActivityRow } from '../components/ActivityRow';
import { NumberField } from '../components/NumberField';
import { TabBar } from '../components/TabBar';
import { accountName } from '../lib/accounts';
import { CASH_COLOR } from '../lib/colors';
import { bookSplit } from '../state/bookSplit';
import { activityFeed, dayLabel, groupByDay, type ActivityItem } from '../state/activity';
import { ringGap } from '../state/ringGap';
import { MoveMoneyButton } from '../components/AccountOutflow';
import { money, pct, pctSigned, shortDate } from '../lib/format';
import { moverSentence } from '../state/moveAlerts';
import { reminderSentence } from '../state/reminders';
import { useDisplay } from '../state/display';
import { useStore } from '../state/store';
import { scrollToSection } from '../lib/scroll';
import type { Nav } from '../App';
import type { CustomRange, RangeKey } from '../state/series';

/** How many entries the recent list on this screen carries. */
const RECENT = 8;

/* What the book is worth, and what it is worth it in — one screen.
 *
 * These were two tabs, and the split was a poor one: opening the app to a total
 * with no holdings under it meant a tap to answer the obvious next question, and
 * the tab bar spent two of its four slots on halves of the same thought. The
 * line leads because where the money has been is the question you arrive with;
 * the list follows because what it is in is the one you ask next. The ring
 * answers neither on its own, so it sits behind a tap. */
export function Value({ nav }: { nav: Nav }) {
  const { book, state, alerts, movers, reminders, setCash, saveSettings } = useStore();

  // Breaches that came and went, newest first.
  const past = state.data.alertLog
    .filter((e) => e.resolvedAt)
    .sort((a, b) => (b.resolvedAt ?? '').localeCompare(a.resolvedAt ?? ''));
  const { currency, toDisplay } = useDisplay();
  const [selected, setSelected] = useState<string | null>(null);
  /* This screen had nothing to report with until money could be moved from it. */
  const [toast, setToast] = useState<string | null>(null);
  const say = (m: string) => { setToast(m); window.setTimeout(() => setToast(null), 3600); };
  const [showSplit, setShowSplit] = useState(false);
  /* The window the chart is on, carried to the full card so opening it does not
     reset the range being looked at. A ref: it changes nothing on this screen. */
  const chartWindow = useRef<{ range: RangeKey; custom: CustomRange } | null>(null);
  /* Which picture of the same split. The ring is good at "a few names are most
     of it" and bad at ranking the rest against each other; columns on a shared
     baseline are the reverse. Remembered, because it is a preference rather
     than a mode you step through. */
  const asBars = state.settings.splitAsBars === true;
  // Absent means on — a ring of unlabelled colours is what the names replaced.
  const ringLabels = state.settings.donutLabels !== false;

  /* Company marks, by fetch symbol. Built once here rather than searched per
     row: a dozen rows times a dozen marks is nothing, and a map is what the
     lookup wants to be. */
  const logos = useMemo(
    () => new Map(state.logos.filter((l) => l.dataUrl).map((l) => [l.symbol, l.dataUrl!])),
    [state.logos],
  );

  const { totals, positions } = book;

  /* The last few things that happened, under the positions they happened to.
     The whole list is one tap away; this is the part checked on the way past.
     Each collection named rather than `state.data` whole, for the same reason
     as on the History screen. */
  const { trades, dividends, cashflows, conversions, transfers, perps, holdings } = state.data;
  const recent = useMemo(
    () => groupByDay(
      activityFeed({ trades, dividends, cashflows, conversions, transfers, perps, holdings }).slice(0, RECENT),
    ),
    [trades, dividends, cashflows, conversions, transfers, perps, holdings],
  );
  const openRecord = (item: ActivityItem) => {
    switch (item.source.type) {
      // Corrected on History, which carries the trade form.
      case 'trade': nav.go({ name: 'activity' }); return;
      case 'dividend': nav.go({ name: 'dividends' }); return;
      case 'perp': nav.go({ name: 'contract', id: item.source.id }); return;
      default: nav.go({ name: 'settings', group: 'money' });
    }
  };
  const openPosition = (ticker: string) => nav.go({ name: 'position', ticker });
  /* Futures as one pillar — the whole wallet, counted as committed — and cash
     as only what sits outside it. See `state/bookSplit.ts`. */
  const split = bookSplit(totals);

  const slices: Slice[] = [
    ...positions.map((p) => ({
      key: p.ticker, label: p.ticker, value: p.marketValue, weight: p.weight, color: p.color,
      ...(logos.get(p.yahooSymbol) ? { logo: logos.get(p.yahooSymbol)! } : {}),
    })),
    ...(split.spareCashUsd > 0 ? [{
      key: '__cash', label: 'Cash', value: split.spareCashUsd,
      weight: split.spareCashPct, color: CASH_COLOR,
    }] : []),
    ...(split.futuresUsd > 0 ? [{
      key: '__perp', label: 'Futures', short: 'Futures',
      value: split.futuresUsd,
      weight: split.futuresPct,
      color: '#5B6880',
    }] : []),
  ];

  const picked = selected ? slices.find((s) => s.key === selected) ?? null : null;
  /* Whether the arcs actually add up to the figure above them. Null, and
     silent, whenever they do — which is every book without a balance below
     zero. */
  const gap = ringGap(slices, totals.totalUsd);

  return (
    <div className="screen" data-tabbed="true">
      <Header
        onOpenSettings={() => nav.go({ name: 'settings' })}
        onOpenDetails={() => nav.go({
          name: 'valueDetails',
          ...(chartWindow.current ? {
            range: chartWindow.current.range,
            from: chartWindow.current.custom.from,
            to: chartWindow.current.custom.to,
          } : {}),
        })}
      />

      <ValuePanel
        compact
        toDisplay={toDisplay}
        currency={currency}
        onRangeChange={(range, custom) => { chartWindow.current = { range, custom }; }}
        onShowSplit={() => {
          setShowSplit(true);
          setSelected(null);
          // Opening it below the fold and leaving the reader where they were is
          // the same as not opening it.
          window.setTimeout(() => scrollToSection('holdings'), 60);
        }}
      />

      <div className="fab-row">
        <button className="btn btn-block" onClick={() => nav.go({ name: 'activity' })}>
          History
        </button>
        <button className="btn btn-primary btn-block" onClick={() => nav.go({ name: 'add' })}>
          Add a position
        </button>
      </div>

      <section id="holdings">
        <div className="section-head">
          <h2>Holdings</h2>
          <button
            className="btn btn-ghost btn-sm"
            aria-expanded={showSplit}
            onClick={() => { setShowSplit((s) => !s); setSelected(null); }}
          >
            {showSplit ? 'Hide the split' : 'Show the split'}
          </button>
        </div>

        {showSplit ? (
          <div className="card hero" style={{ marginBottom: 10 }}>
            <div className="hero-label">Invested</div>
            <div className="hero-total num">{money(toDisplay(split.committedUsd), currency)}</div>
            <div className="hero-change">
              <span className="dim" style={{ fontSize: 12.5 }}>
                {/* The share the figure above it actually is. It used to be
                    `100 - cashPct`, which treats cash as the only part of the
                    book that is not a shareholding — so a book with contracts
                    open reported its holdings as 100.0% of itself while the
                    ring below drew a 4.0% futures slice out of the same total,
                    and the two disagreed by the margin posted. */}
                {positions.length} positions · {pct(split.sharesPct)} shares
                {split.futuresUsd > 0 ? <> · {pct(split.futuresPct)} futures</> : null}
                {/* The spare cash, said here rather than only on the card
                    further down. It is the rest of the same sentence: this is
                    what the holdings are, and that is what is not in them. Its
                    own slice exists but is usually far too thin to label, so
                    the ring cannot say it.
                    Below zero it is not "free" anything — that word would make
                    a shortfall sound like money available to spend, which is
                    the one reading it must not have. */}
                {split.spareCashUsd >= 0
                  ? <> · <span className="num">{money(toDisplay(split.spareCashUsd), currency)}</span> free</>
                  : <> · cash <span className="num">{money(toDisplay(-split.spareCashUsd), currency)}</span> below zero</>}
              </span>
            </div>

            {asBars ? (
              <>
                <WeightBars
                  slices={slices}
                  selected={selected}
                  onSelect={(key) => {
                    setSelected(key);
                    if (key && key === selected && !key.startsWith('__')) openPosition(key);
                  }}
                  currency={currency}
                  toDisplay={toDisplay}
                />
                {/* What the ring says in its middle. The columns have no middle,
                    so it goes underneath — and it is the same sentence either
                    way, written once. */}
                <div className="dim" style={{ textAlign: 'center', fontSize: 12.5, marginTop: 8 }}>
                  {picked
                    ? <><span className="num">{picked.label}</span>{' · '}
                        <span className="num">{money(toDisplay(picked.value), currency)}</span>
                        {' · '}<span className="num">{pct(picked.weight)}</span></>
                    : <>Tallest first. Tap a column.</>}
                </div>
              </>
            ) : (
            <Donut
              slices={slices}
              selected={selected}
              onSelect={(key) => {
                setSelected(key);
                if (key && key === selected && !key.startsWith('__')) openPosition(key);
              }}
              onFlip={() => setSelected(null)}
              flip={{
                hint: selected ? 'TAP TO CLEAR' : '',
                label: 'Clear the selected slice',
              }}
              centre={picked
                ? { label: picked.label, value: money(toDisplay(picked.value), currency), sub: pct(picked.weight) }
                : { label: 'Positions', value: String(positions.length), sub: 'tap a slice' }}
              currency={currency}
              labels={ringLabels}
            />
            )}

            {/* The ring draws what can be drawn, and an arc has no negative
                length — so a balance below zero is left out of it while staying
                in the total above. The ring then closes into a full circle that
                adds up to more than the book, with nothing on screen to say a
                slice is missing. See `state/ringGap.ts`. */}
            {gap ? (
              <div className="hint" style={{ marginTop: 8, textAlign: 'center' }}>
                The ring comes to <span className="num">{money(toDisplay(gap.ringUsd), currency)}</span>
                {' '}and the book to <span className="num">{money(toDisplay(gap.bookUsd), currency)}</span>.
                {' '}{gap.differenceUsd < 0
                  ? `Cash is below zero by ${money(toDisplay(-gap.differenceUsd), currency)}, and that
                     cannot be drawn as a slice, so the ring is what you hold rather than what the
                     book is worth.`
                  : `${money(toDisplay(gap.differenceUsd), currency)} of the book is not in any
                     slice.`}
              </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 2,
                          flexWrap: 'wrap' }}>
              <button
                className="btn btn-ghost btn-sm"
                aria-pressed={asBars}
                onClick={() => void saveSettings({ splitAsBars: !asBars })}
              >
                {asBars ? 'Show the ring' : 'Show it as bars'}
              </button>
              {/* The ring's names crowd; the columns are named by construction,
                  so the switch has nothing to do there. */}
              {!asBars ? (
                <button
                  className="btn btn-ghost btn-sm"
                  aria-pressed={ringLabels}
                  onClick={() => void saveSettings({ donutLabels: !ringLabels })}
                >
                  {ringLabels ? 'Hide the names' : 'Show the names'}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <HoldingsList
          positions={positions}
          onOpen={openPosition}
          toDisplay={toDisplay}
          currency={currency}
          logoFor={(p) => logos.get(p.yahooSymbol)}
        />

        {/* The credit the logo service asks for in exchange for being free, and
            it asks for it visibly. Shown where the marks are, and only when
            some are actually on screen — a credit for something not being used
            would be noise. */}
        {logos.size > 0 ? (
          <div className="dim" style={{ fontSize: 10.5, marginTop: 8, textAlign: 'right' }}>
            Logos by{' '}
            <a href="https://www.allinvestview.com/tools/ticker-logos/"
               target="_blank" rel="noreferrer">AllInvestView</a>
          </div>
        ) : null}

        {/* Every free balance, broker by broker.
 
            One lump reading "Cash $48" is not answerable: there is no way to
            tell from it which account holds what, and these are numbers you
            type in rather than ones the app works out — so when one is wrong,
            the screen has to say which one to go and fix. */}
        <div className="card card-pad" style={{ marginTop: 8 }}>
          <div className="kv">
            <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>Cash</span>
            <span className="v num" style={{ fontWeight: 600 }}>
              {money(toDisplay(totals.cashUsd), currency)} · {pct(totals.cashPct)}
            </span>
          </div>

          {/* Where the money actually is.
           
              "I paid in forty thousand and this says eleven dollars" is the
              question this card gets asked, and until now it answered only the
              last third of it. The money is not missing — it is in the
              positions — but a card showing one of the three figures cannot say
              so, and the reasonable conclusion from one number is that
              something was lost. All three, on one line, and the question stops
              being asked. */}
          {totals.contributedUsd > 0 ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>
              Paid in <span className="num">{money(toDisplay(totals.contributedUsd), currency)}</span>
              {' · '}in positions{' '}
              <span className="num">
                {money(toDisplay(split.committedUsd), currency)}
              </span>
              {' · '}free <span className="num">{money(toDisplay(split.spareCashUsd), currency)}</span>
              {split.futuresUsd > 0 ? ' outside futures' : ''}.
              {' '}The three do not add up to each other, and are not meant to. What is in positions
              is what they are worth today, not what was put into them.
            </div>
          ) : null}

          {book.cashByAccount.length > 0 ? (
            <>
              <div className="divider" />
              {book.cashByAccount.map((c) => {
                const record = state.data.accounts.find((a) => a.id === c.account);
                return (
                  <div className="kv" key={c.account} style={{ marginBottom: 8 }}>
                    <span className="k">{accountName(state.data.accounts, c.account)}</span>
                    {record?.isFutures ? (
                      <span className="dim" style={{ fontSize: 11 }}>free on the exchange</span>
                    ) : null}
                    {/* Editable here rather than only behind a link. This is the
                        screen the wrong number is being read on, and these are
                        figures nothing works out — sending someone to another
                        screen to correct what they are looking at is a step
                        that exists for no reason. */}
                    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <NumberField
                        aria-label={`Free cash on ${accountName(state.data.accounts, c.account)}`}
                        value={c.amount}
                        style={{ width: 108, textAlign: 'right', padding: '6px 8px', minHeight: 0 }}
                        onChange={(n) => void setCash(c.account, Number.isFinite(n) ? n : 0, c.currency)}
                      />
                      <span className="dim num" style={{ fontSize: 11.5, width: 30 }}>{c.currency}</span>
                    </span>
                  </div>
                );
              })}
            </>
          ) : null}

          {/* Moving money between your own accounts, or out of the book. Here
              because this is the card those balances are read off, and the
              commonest way for one of them to be wrong is a transfer nobody
              wrote down: it looks like a loss on one side and a deposit on the
              other, and neither is what happened. */}
          <div style={{ marginTop: 10 }}>
            <MoveMoneyButton say={say} />
          </div>

          {book.cashByAccount.some((c) => c.amount < 0) ? (
            /* Shown rather than clamped. A balance below zero means the book has
               seen money spent that it never saw arrive — which is the single
               most useful thing it can tell you about its own records, and
               hiding it at zero would turn a visible gap into a silent one. */
            <div className="note" data-tone="warn" style={{ marginTop: 10 }}>
              {book.cashByAccount.filter((c) => c.amount < 0)
                .map((c) => accountName(state.data.accounts, c.account)).join(', ')}
              {' '}is below zero. That does not mean you are overdrawn. It means this book has
              recorded money going out that it never recorded coming in. Usually a deposit that
              was never entered, or a purchase entered at the total rather than the price per
              share. Type the balance your broker shows over it, and check the buys in the trade
              log against what you actually paid.
            </div>
          ) : null}

          <div className="dim" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.45 }}>
            These move on their own now: paying money in raises one, buying takes the money out,
            selling and dividends put it back, and a contract posts or releases its margin. What
            they cannot know about is anything you did without recording it here, so type over any
            of them whenever the book and your broker disagree.
          </div>
        </div>
      </section>

      {recent.length > 0 ? (
        <section id="recent">
          <div className="section-head">
            <h2>Recent activity</h2>
            <button className="btn btn-ghost btn-sm" onClick={() => nav.go({ name: 'activity' })}>
              See all
            </button>
          </div>
          {recent.map((group, i) => (
            <div key={group.day}>
              <div className="activity-day" style={i === 0 ? { marginTop: 0 } : undefined}>
                {dayLabel(group.day)}
              </div>
              <div className="card activity-list">
                {group.items.map((item) => (
                  <ActivityRow key={item.id} item={item} open={false}
                               nameOf={(id) => accountName(state.data.accounts, id)}
                               onOpen={() => openRecord(item)} />
                ))}
              </div>
            </div>
          ))}
        </section>
      ) : null}

      <section>
        <div className="section-head"><h2>Themes</h2></div>
        <ThemeBars themes={book.themes} toDisplay={toDisplay} currency={currency} />
      </section>

      {/* What the market did today, kept apart from what your limits say. One is
          news, the other is a decision you already made — putting them in one
          list would blur which is which. */}
      {movers.length > 0 ? (
        <section>
          <div className="section-head">
            <h2>Moved today</h2>
            <span className="meta">past your threshold</span>
          </div>
          <div className="hscroll">
            {movers.map((m) => (
              <button key={m.symbol} className="earn-card"
                      onClick={() => (m.owned ? openPosition(m.label) : nav.go({ name: 'radar' }))}>
                <div className={`earn-days num ${m.changePct >= 0 ? 'up' : 'down'}`}>
                  {m.changePct >= 0 ? '▲' : '▼'} {pctSigned(m.changePct)}
                </div>
                <div className="earn-ticker">{m.label}</div>
                <div className="earn-date">{m.owned ? 'held' : 'watching'}</div>
                <div className="earn-date" style={{ marginTop: 'auto' }}>{moverSentence(m)}</div>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* A calendar, not news: what is about to happen rather than what already
          did. Kept apart from the moves above for the same reason both are kept
          apart from the alerts below — they are three different kinds of fact. */}
      {reminders.length > 0 ? (
        <section>
          <div className="section-head">
            <h2>Due soon</h2>
            <span className="meta">reports and ex-dates</span>
          </div>
          <div className="hscroll">
            {reminders.map((r) => (
              <button key={r.key} className="earn-card"
                      onClick={() => (r.owned ? openPosition(r.label) : nav.go({ name: 'earnings' }))}>
                <div className="earn-days num">
                  {r.away === 0 ? 'Today' : r.away}
                  {r.away === 0 ? null : <small>{r.away === 1 ? 'day' : 'days'}</small>}
                </div>
                <div className="earn-ticker">{r.label}</div>
                <div className="earn-date">{r.kind === 'earnings' ? 'reports' : 'ex-dividend'}</div>
                <div className="earn-date">{shortDate(r.date)}</div>
                {r.needsPlan ? <div className="earn-plan">WRITE YOUR PLAN FIRST</div> : null}
              </button>
            ))}
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
            {reminderSentence(reminders[0]!)}
          </div>
        </section>
      ) : null}

      {/* Last, deliberately: what nags sits at the foot. The header carries the
          count and jumps here, so nothing is buried by being placed low. */}
      <section id="alerts">
        <div className="section-head">
          <h2>Alerts</h2>
          <span className="meta">
            {alerts.length === 0
              ? 'all clear'
              : `${alerts.filter((a) => a.severity === 'critical').length} critical`}
          </span>
        </div>
        <Alerts alerts={alerts} onOpen={openPosition} />
      </section>

      {past.length > 0 ? (
        <section>
          <div className="section-head">
            <h2>Breaches that cleared</h2>
            <span className="meta">{past.length}</span>
          </div>
          <div className="card card-pad">
            {past.slice(0, 8).map((e, i) => (
              <div className="kv" key={e.id + e.firstSeen}
                   style={{ marginBottom: i === Math.min(past.length, 8) - 1 ? 0 : 8 }}>
                <span className="k" style={{ fontSize: 12 }}>{e.headline}</span>
                <span className="dim num" style={{ fontSize: 10.5, marginLeft: 'auto' }}>
                  {shortDate(e.firstSeen)} → {shortDate(e.resolvedAt!)}
                </span>
              </div>
            ))}
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
            Kept so that a limit you went past and then fixed is still answerable a week later.
            It records that there was an episode, not whether anything should have been done.
          </div>
        </section>
      ) : null}

      <p className="dim" style={{ fontSize: 11, textAlign: 'center', marginTop: 28, lineHeight: 1.5 }}>
        This book records what you already did at your broker.
        It places no orders and holds no account credentials.
      </p>

      {toast ? <div className="toast">{toast}</div> : null}

      <TabBar current="value" go={nav.go} />
    </div>
  );
}
